import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getResaleItem, resaleItemsQueryKeys } from '../../api/resaleItems';
import {
  RESALE_ITEMS_SEARCH_DEFAULTS,
  parseResaleItemsSearch,
} from '../../hooks/useResaleItemsRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type ResaleItemManagement from '../../components/ResaleItemManagement/index';
import type ResaleItemDetails from '../../components/ResaleItemDetails/index';
import type EditResaleItem from '../../components/EditResaleItem/index';
import type { ApiResaleItem } from '../../api/resaleItems';

export const RESALE_ITEM_STALE_TIME_MS = 30_000;

// Shared query for /resale_items/$id/{details,edit}. We don't reuse `useResaleItem`
// because that hook applies a `select` mapper — the two route components feed
// the raw ApiResaleItem into their child components, which do their own
// mapping. Both routes still share the cache via `resaleItemsQueryKeys.detail`.
export function useResaleItemRaw(idNum: number, seed: ApiResaleItem | undefined) {
  return useQuery({
    queryKey: resaleItemsQueryKeys.detail(idNum),
    queryFn: () => getResaleItem(idNum),
    staleTime: RESALE_ITEM_STALE_TIME_MS,
    ...(seed
      ? { initialData: { resale_item: seed }, initialDataUpdatedAt: Date.now() }
      : {}),
  });
}

export const resaleItemsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resale_items',
  validateSearch: parseResaleItemsSearch,
  search: { middlewares: [stripSearchParams(RESALE_ITEMS_SEARCH_DEFAULTS)] },
  component: function ResaleItemsIndexRoute() {
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    useDocumentTitle(t('common.page_titles.resale_items'));
    const [Component, setComponent] = useState<typeof ResaleItemManagement | null>(null);
    useEffect(() => {
      import('../../components/ResaleItemManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const resaleItemDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resale_items/$id/details',
  component: function ResaleItemDetailsRoute() {
    const params = resaleItemDetailsRoute.useParams();
    const { initialResaleItem, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialResaleItem && initialResaleItem.id === idNum ? initialResaleItem : undefined;
    const { data } = useResaleItemRaw(idNum, seed);
    useDocumentTitle(data?.resale_item?.name ?? t('common.page_titles.resale_items'));
    const [Component, setComponent] = useState<typeof ResaleItemDetails | null>(null);
    useEffect(() => {
      import('../../components/ResaleItemDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const apiItem = data?.resale_item;
    if (!Component || !apiItem) {
      return <DetailsSkeleton label={t('common.loading')} />;
    }
    return <Component resaleItemId={idNum} initialResaleItem={apiItem} currentUser={currentUser} />;
  },
});

export const resaleItemEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resale_items/$id/edit',
  // RBAC: the edit page is a write-only surface. Read-only users (no
  // resale_items.write) are redirected to the read-only details page before the
  // component or its loader runs. The server policy enforces this too.
  beforeLoad: requireCapability(CAPABILITIES.RESALE_ITEMS_WRITE, ({ params }) => ({
    to: '/resale_items/$id/details',
    params: { id: params.id },
  })),
  component: function ResaleItemEditRoute() {
    const params = resaleItemEditRoute.useParams();
    const { initialResaleItem } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialResaleItem && initialResaleItem.id === idNum ? initialResaleItem : undefined;
    const { data } = useResaleItemRaw(idNum, seed);
    useDocumentTitle(
      data?.resale_item ? t('common.page_titles.edit', { name: data?.resale_item.name }) : t('common.page_titles.resale_items'),
    );
    const [Component, setComponent] = useState<typeof EditResaleItem | null>(null);
    useEffect(() => {
      import('../../components/EditResaleItem/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const apiItem = data?.resale_item;
    if (!Component || !apiItem) {
      return <EditSkeleton label={t('common.loading')} />;
    }
    return <Component resaleItemId={idNum} initialResaleItem={apiItem} />;
  },
});
